import { createContext, useContext, useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { useAuthUser } from "./AuthProvider";
import { syncCurrentUserSession } from "../helpers/firebaseAuth";

const AuthStatusContext = createContext({ 
    isAuthenticated: false, 
    isLoading: true,
    isEmailVerified: false,
    user: null,
    checkAuth: async () => {} 
});

export function AuthStatusProvider({ children }) {
    const authUser = useAuthUser(); // shared AuthProvider listener — avoids a second onAuthStateChanged subscription
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [user, setUser] = useState(null);

    const syncState = (firebaseUser) => {
        setUser(firebaseUser || null);
        setIsAuthenticated(!!firebaseUser);
        setIsEmailVerified(!!firebaseUser?.emailVerified);
        setIsLoading(false);
    };

    useEffect(() => {
        if (authUser === undefined) return; // AuthProvider still initializing
        syncState(authUser);
    }, [authUser]);

    const checkAuth = async () => {
        const auth = getAuth();
        const currentUser = auth.currentUser;

        if (currentUser) {
            await currentUser.reload();
            syncState(auth.currentUser);

            if (auth.currentUser?.emailVerified) {
                await syncCurrentUserSession().catch(() => {});
            }
        } else {
            syncState(null);
        }

        return {
            isAuthenticated: !!auth.currentUser,
            isEmailVerified: !!auth.currentUser?.emailVerified,
            user: auth.currentUser || null,
        };
    };

    return (
        <AuthStatusContext.Provider value={{ isAuthenticated, isLoading, isEmailVerified, user, checkAuth }}>
            {children}
        </AuthStatusContext.Provider>
    );
}

export function useAuthStatus() {
    return useContext(AuthStatusContext);
}
